export type GameNameComboRow = {
  gameName: string;
  gameTitle: string;
  /**
   * Effective manufacturer (slotcheckManufacturer when set, else raw) — the row
   * key and the Set / Unmap match value. ms-casino groups and matches by it.
   */
  manufacturer: string;
  displayManufacturer: string;
  /** Raw machine manufacturer codes folded into this row (from ms-casino). */
  rawManufacturers?: string[];
  slotcheckGameName: string;
  slotcheckGameTitle: string;
  egmCount: number;
  activeCount: number;
  isPartial?: boolean;
};

export type SortKey = 'name' | 'egmCount' | 'activeCount';

export function comboRowKey(row: {
  gameName: string;
  gameTitle: string;
  manufacturer: string;
}): string {
  return `${row.gameName}\0${row.gameTitle}\0${row.manufacturer}`;
}

/** Case-insensitive partial match across raw + SlotCheck name/title (+ manufacturer). */
export function gameNameRowMatchesQuery(row: GameNameComboRow, q: string): boolean {
  const needle = String(q || '')
    .trim()
    .toLowerCase();
  if (!needle) return true;
  const fields = [
    row.gameName,
    row.gameTitle,
    row.slotcheckGameName,
    row.slotcheckGameTitle,
    row.manufacturer,
    row.displayManufacturer,
  ];
  return fields.some((f) => String(f || '').toLowerCase().includes(needle));
}

/** Instant client-side filter of the currently loaded page. */
export function filterGameNameRows(
  rows: GameNameComboRow[],
  q: string,
  manufacturerFilter?: string
): GameNameComboRow[] {
  const mfrNeedle = String(manufacturerFilter || '')
    .trim()
    .toLowerCase();
  return (rows || []).filter((r) => {
    if (!gameNameRowMatchesQuery(r, q)) return false;
    if (!mfrNeedle) return true;
    const mfr = String(r.displayManufacturer || r.manufacturer || '').toLowerCase();
    return mfr.includes(mfrNeedle);
  });
}

/** Client-side sort of the current page. */
export function sortGameNameRows(
  rows: GameNameComboRow[],
  sortKey: SortKey,
  sortDir: 'asc' | 'desc'
): GameNameComboRow[] {
  const dir = sortDir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (sortKey === 'name') {
      const n = a.gameName.localeCompare(b.gameName);
      if (n) return dir * n;
      const t = a.gameTitle.localeCompare(b.gameTitle);
      if (t) return dir * t;
      return dir * a.manufacturer.localeCompare(b.manufacturer);
    }
    return dir * ((a[sortKey] || 0) - (b[sortKey] || 0));
  });
}

/**
 * Active EGMs only: keep combos with at least one active EGM (activeCount > 0).
 * Same rule as ms-casino (isActive missing/null counts as active there).
 */
export function filterActiveGameNameRows(rows: GameNameComboRow[]): GameNameComboRow[] {
  return (rows || []).filter((r) => (Number(r.activeCount) || 0) > 0);
}

/** Filter then sort the loaded page (instant as-you-type; server search follows). */
export function filterSortGameNameRows(
  rows: GameNameComboRow[],
  q: string,
  manufacturerFilter: string,
  sortKey: SortKey,
  sortDir: 'asc' | 'desc',
  activeOnly = false
): GameNameComboRow[] {
  const filtered = filterGameNameRows(rows, q, manufacturerFilter);
  return sortGameNameRows(
    activeOnly ? filterActiveGameNameRows(filtered) : filtered,
    sortKey,
    sortDir
  );
}

export type SlotcheckAutocompleteList = {
  names?: string[] | null;
  titles?: string[] | null;
};

/**
 * Unique SlotCheck Game Name / Theme values for autocomplete: SlotCheck values on
 * the given rows plus any extra lists — the full server list
 * (payload.slotcheckGameNames / slotcheckGameTitles, never narrowed by search,
 * filters or paging) and values just saved with Set on this screen.
 */
export function collectSlotcheckAutocomplete(
  rows: GameNameComboRow[],
  extras: Array<SlotcheckAutocompleteList | null | undefined> = []
): {
  names: string[];
  titles: string[];
} {
  const names = new Set<string>();
  const titles = new Set<string>();
  for (const r of rows || []) {
    const n = String(r.slotcheckGameName || '').trim();
    const t = String(r.slotcheckGameTitle || '').trim();
    if (n) names.add(n);
    if (t) titles.add(t);
  }
  for (const extra of extras || []) {
    for (const v of extra?.names || []) {
      const n = String(v || '').trim();
      if (n) names.add(n);
    }
    for (const v of extra?.titles || []) {
      const t = String(v || '').trim();
      if (t) titles.add(t);
    }
  }
  return {
    names: Array.from(names).sort((a, b) => a.localeCompare(b)),
    titles: Array.from(titles).sort((a, b) => a.localeCompare(b)),
  };
}

/**
 * Prefill drafts for Set: unmapped → raw gameName/gameTitle;
 * partial → keep existing SlotCheck value, prefill missing from raw.
 */
export function defaultDraftForRow(row: GameNameComboRow): {
  slotcheckGameName: string;
  slotcheckGameTitle: string;
} {
  const scName = String(row.slotcheckGameName || '').trim();
  const scTitle = String(row.slotcheckGameTitle || '').trim();
  return {
    slotcheckGameName: scName || String(row.gameName || '').trim(),
    slotcheckGameTitle: scTitle || String(row.gameTitle || '').trim(),
  };
}

/**
 * For partials, only send SlotCheck fields that are still blank on the row
 * so the server blank-only filter can match. Unmapped sends both.
 */
export function payloadSlotcheckFields(
  row: GameNameComboRow,
  draftName: string,
  draftTitle: string
): { slotcheckGameName: string; slotcheckGameTitle: string } {
  const hasName = Boolean(String(row.slotcheckGameName || '').trim());
  const hasTitle = Boolean(String(row.slotcheckGameTitle || '').trim());
  return {
    slotcheckGameName: hasName ? '' : draftName.trim(),
    slotcheckGameTitle: hasTitle ? '' : draftTitle.trim(),
  };
}

export function setGameNamesConfirmMessage(
  row: GameNameComboRow,
  slotcheckGameName: string,
  slotcheckGameTitle: string
): string {
  const namePart = slotcheckGameName
    ? `Game Name "${slotcheckGameName}"`
    : null;
  const titlePart = slotcheckGameTitle
    ? `Game Theme "${slotcheckGameTitle}"`
    : null;
  const setParts = [namePart, titlePart].filter(Boolean).join(' and ');
  return `Update ${row.egmCount.toLocaleString()} EGM${
    row.egmCount === 1 ? '' : 's'
  } matching gameName "${row.gameName}", gameTitle "${row.gameTitle}", manufacturer "${
    row.manufacturer
  }"${rawCodesSuffix(row)} → set ${setParts}? Raw gameName, gameTitle, and manufacturer are never changed.`;
}

/**
 * "Raw: ATI, XARISTOCRAT-LEASED" when the row's raw manufacturer codes differ
 * from the manufacturer shown; '' when they are the same (or unknown).
 */
export function rawManufacturerCodesLabel(row: {
  manufacturer: string;
  displayManufacturer?: string;
  rawManufacturers?: string[] | null;
}): string {
  const shown = String(row.displayManufacturer || row.manufacturer || '').trim();
  const codes = Array.from(
    new Set((row.rawManufacturers || []).map((c) => String(c ?? '').trim()))
  );
  if (!codes.length) return '';
  if (codes.length === 1 && codes[0] === shown) return '';
  return `Raw: ${codes.map((c) => c || '(blank)').join(', ')}`;
}

/** Confirm-dialog suffix listing raw codes, e.g. ' (raw: ATI, XARISTOCRAT-LEASED)'. */
function rawCodesSuffix(row: GameNameComboRow): string {
  const label = rawManufacturerCodesLabel(row);
  return label ? ` (${label.replace(/^Raw:/, 'raw:')})` : '';
}

export function isMissingSlotcheckName(row: GameNameComboRow): boolean {
  return !String(row.slotcheckGameName || '').trim();
}

export function isMissingSlotcheckTitle(row: GameNameComboRow): boolean {
  return !String(row.slotcheckGameTitle || '').trim();
}

/**
 * Add values just saved with Set to the local autocomplete list (so they are
 * options right away, before / regardless of the refresh). Same object when
 * nothing new was added.
 */
export function addRecentSlotcheckValues(
  prev: { names: string[]; titles: string[] },
  slotcheckGameName: string,
  slotcheckGameTitle: string
): { names: string[]; titles: string[] } {
  const n = String(slotcheckGameName || '').trim();
  const t = String(slotcheckGameTitle || '').trim();
  const addName = Boolean(n) && !prev.names.includes(n);
  const addTitle = Boolean(t) && !prev.titles.includes(t);
  if (!addName && !addTitle) return prev;
  return {
    names: addName ? [...prev.names, n] : prev.names,
    titles: addTitle ? [...prev.titles, t] : prev.titles,
  };
}

/** Query params for GetGameNamesManagement (omits empty filters; activeOnly only when on). */
export function buildGameNamesListQuery(opts: {
  q?: string;
  manufacturer?: string;
  activeOnly?: boolean;
  limit?: number;
  offset?: number;
}): { q?: string; manufacturer?: string; activeOnly?: boolean; limit?: number; offset?: number } {
  const q = String(opts.q || '').trim();
  const manufacturer = String(opts.manufacturer || '').trim();
  const out: {
    q?: string;
    manufacturer?: string;
    activeOnly?: boolean;
    limit?: number;
    offset?: number;
  } = {};
  if (q) out.q = q;
  if (manufacturer) out.manufacturer = manufacturer;
  if (opts.activeOnly) out.activeOnly = true;
  if (opts.limit != null) out.limit = opts.limit;
  if (opts.offset != null) out.offset = opts.offset;
  return out;
}

/**
 * Manufacturer Filter autocomplete options — effective manufacturers
 * (slotcheckManufacturer else manufacturer), same notion as MM display.
 */
export function manufacturerFilterOptions(names: string[] | undefined | null): string[] {
  const set = new Set<string>();
  for (const n of names || []) {
    const t = String(n || '').trim();
    if (t) set.add(t);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

// ---------------------------------------------------------------------------
// SlotCheck tab accordion: groups by SlotCheck (Game Name, Game Theme) pair,
// each listing the raw combos mapped into it with an Unmap action.
// ---------------------------------------------------------------------------

export type GameNameMappingGroup = {
  slotcheckGameName: string;
  slotcheckGameTitle: string;
  egmCount: number;
  activeCount: number;
  isPartial?: boolean;
  mappedFrom: GameNameComboRow[];
};

/** Stable accordion key for a SlotCheck pair (exact trimmed values). */
export function gameNameGroupKey(group: {
  slotcheckGameName: string;
  slotcheckGameTitle: string;
}): string {
  return `${String(group.slotcheckGameName || '').trim()}\0${String(
    group.slotcheckGameTitle || ''
  ).trim()}`;
}

/** Busy key for one raw combo's Unmap inside one group. */
export function unmapRowKey(row: GameNameComboRow): string {
  return `${gameNameGroupKey(row)}\0${comboRowKey(row)}`;
}

/**
 * Group managed combos by exact SlotCheck pair (same rule as the API's
 * managedGroups). Counts are summed from the combos in the group.
 */
export function groupGameNameRows(rows: GameNameComboRow[]): GameNameMappingGroup[] {
  const map = new Map<string, GameNameMappingGroup>();
  for (const row of rows || []) {
    const scName = String(row.slotcheckGameName || '').trim();
    const scTitle = String(row.slotcheckGameTitle || '').trim();
    if (!scName && !scTitle) continue;
    const key = gameNameGroupKey({ slotcheckGameName: scName, slotcheckGameTitle: scTitle });
    const group = map.get(key) || {
      slotcheckGameName: scName,
      slotcheckGameTitle: scTitle,
      egmCount: 0,
      activeCount: 0,
      isPartial: (scName !== '' && scTitle === '') || (scName === '' && scTitle !== ''),
      mappedFrom: [],
    };
    group.egmCount += Number(row.egmCount) || 0;
    group.activeCount += Number(row.activeCount) || 0;
    group.mappedFrom.push(row);
    map.set(key, group);
  }
  const groups = Array.from(map.values());
  for (const g of groups) g.mappedFrom = sortGameNameRows(g.mappedFrom, 'name', 'asc');
  return groups;
}

/** Groups from the API (managedGroups), or derived from the flat managed list (older API). */
export function managedGroupsFromPayload(
  payload:
    | { managed?: GameNameComboRow[]; managedGroups?: GameNameMappingGroup[] }
    | null
    | undefined
): GameNameMappingGroup[] {
  if (payload && Array.isArray(payload.managedGroups)) return payload.managedGroups;
  return groupGameNameRows(payload?.managed || []);
}

/** Sort groups: name = SlotCheck Game Name then Game Theme; counts = group totals. */
export function sortGameNameGroups(
  groups: GameNameMappingGroup[],
  sortKey: SortKey,
  sortDir: 'asc' | 'desc'
): GameNameMappingGroup[] {
  const dir = sortDir === 'asc' ? 1 : -1;
  return [...groups].sort((a, b) => {
    if (sortKey === 'name') {
      const n = a.slotcheckGameName.localeCompare(b.slotcheckGameName);
      if (n) return dir * n;
      return dir * a.slotcheckGameTitle.localeCompare(b.slotcheckGameTitle);
    }
    return dir * ((a[sortKey] || 0) - (b[sortKey] || 0));
  });
}

/**
 * Instant as-you-type filter for the SlotCheck tab: filter the mapped combos with
 * the same rules as the flat list (raw + SlotCheck name/title + manufacturer, and
 * the manufacturer filter), then regroup and sort. A group stays when any of its
 * combos match; a SlotCheck name/title match keeps the whole group.
 */
export function filterSortGameNameGroups(
  groups: GameNameMappingGroup[],
  q: string,
  manufacturerFilter: string,
  sortKey: SortKey,
  sortDir: 'asc' | 'desc',
  activeOnly = false
): GameNameMappingGroup[] {
  const rows = (groups || []).flatMap((g) =>
    (g.mappedFrom || []).map((r) => ({
      ...r,
      // Children always carry the group's pair (older payloads may omit them).
      slotcheckGameName: r.slotcheckGameName ?? g.slotcheckGameName,
      slotcheckGameTitle: r.slotcheckGameTitle ?? g.slotcheckGameTitle,
    }))
  );
  // Active EGMs only drops combos with no active EGM, then regroups (a group with
  // no active combos disappears) — same as ms-casino's activeOnly.
  const filtered = filterGameNameRows(rows, q, manufacturerFilter);
  return sortGameNameGroups(
    groupGameNameRows(activeOnly ? filterActiveGameNameRows(filtered) : filtered),
    sortKey,
    sortDir
  );
}

/** Toggle a group key in the expanded set (multi-open accordion). */
export function toggleExpandedGroup(
  expanded: ReadonlySet<string> | string[],
  key: string
): Set<string> {
  const next = new Set(expanded);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** Drop expanded keys whose group disappeared (e.g. last combo unmapped). Same Set if unchanged. */
export function pruneExpandedGroups(
  expanded: Set<string>,
  groups: GameNameMappingGroup[]
): Set<string> {
  const keys = new Set((groups || []).map((g) => gameNameGroupKey(g)));
  let changed = false;
  const next = new Set<string>();
  expanded.forEach((k) => {
    if (keys.has(k)) next.add(k);
    else changed = true;
  });
  return changed ? next : expanded;
}

/**
 * UnsetSlotcheckGameNames body: combo key (raw name/title + effective manufacturer)
 * + the combo's current SlotCheck pair.
 */
export function unmapGameNamesBody(row: GameNameComboRow): {
  gameName: string;
  gameTitle: string;
  manufacturer: string;
  slotcheckGameName: string;
  slotcheckGameTitle: string;
} {
  return {
    gameName: row.gameName,
    gameTitle: row.gameTitle,
    manufacturer: row.manufacturer,
    slotcheckGameName: String(row.slotcheckGameName || '').trim(),
    slotcheckGameTitle: String(row.slotcheckGameTitle || '').trim(),
  };
}

/** Confirm dialog copy for Unmap. */
export function unmapGameNameConfirmMessage(row: GameNameComboRow): string {
  const scName = String(row.slotcheckGameName || '').trim() || '—';
  const scTitle = String(row.slotcheckGameTitle || '').trim() || '—';
  return `Unmap ${row.egmCount.toLocaleString()} EGM${
    row.egmCount === 1 ? '' : 's'
  } matching gameName "${row.gameName}", gameTitle "${row.gameTitle}", manufacturer "${
    row.manufacturer
  }"${rawCodesSuffix(row)} from SlotCheck Game Name "${scName}" / Game Theme "${scTitle}"? They will appear in Unmapped. Raw gameName, gameTitle, and manufacturer are never changed.`;
}
